import { ChevronRight, Sparkles, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { env } from "@/lib/env";

interface DiscountMethodSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMethodSelect: (method: "coupon" | "automatic") => void;
}

export function DiscountMethodSelectionDialog({
  open,
  onOpenChange,
  onMethodSelect,
}: DiscountMethodSelectionDialogProps) {
  const { t } = useTranslation("discounts");

  const handleMethodSelect = (method: "coupon" | "automatic") => {
    onMethodSelect(method);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("methodSelection.title")}</DialogTitle>
          <DialogDescription>{t("methodSelection.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          {/* Menu Discount */}
          <button
            type="button"
            onClick={() => handleMethodSelect("automatic")}
            className="w-full flex items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            <div className="rounded-lg bg-emerald-50 p-2 flex-shrink-0">
              <Sparkles className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium">{t("methodSelection.automatic.title")}</h3>
              <p className="text-xs text-muted-foreground">
                {t("methodSelection.automatic.description")}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </button>

          {/* Coupon Code */}
          {env.flags.showExperimental && (
            <button
              type="button"
              onClick={() => handleMethodSelect("coupon")}
              className="w-full flex items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            >
              <div className="rounded-lg bg-blue-50 p-2 flex-shrink-0">
                <Tag className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium">{t("methodSelection.coupon.title")}</h3>
                <p className="text-xs text-muted-foreground">
                  {t("methodSelection.coupon.description")}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
